import { useCallback, useState } from "react";
import type { Toast } from "primereact/toast";
import type { BannerSwap } from "../../utils/@types";
import { bannerDashboardApi } from "../../api/bannerDashboardApi";

export type SwapRowAnimationPhase = "cancelling" | "removing";

/** Duration of fade-out before the row is removed from the table. */
export const SWAP_ROW_FADE_OUT_MS = 450;

export function useSwapQueueCancellation(options: {
  toast: React.RefObject<Toast | null>;
  setBannerSwapQueue: React.Dispatch<React.SetStateAction<BannerSwap[]>>;
  setFilteredRows: React.Dispatch<React.SetStateAction<BannerSwap[]>>;
}) {
  const { toast, setBannerSwapQueue, setFilteredRows } = options;
  const [rowPhases, setRowPhases] = useState<
    Record<string, SwapRowAnimationPhase>
  >({});

  const cancelSwap = useCallback(
    async (swapUID: string) => {
      setRowPhases((prev) => ({ ...prev, [swapUID]: "cancelling" }));

      try {
        await bannerDashboardApi.updateBannerSwapStatus(swapUID, "cancelled");

        setRowPhases((prev) => ({ ...prev, [swapUID]: "removing" }));

        setBannerSwapQueue((prev) =>
          prev.map((s) =>
            s.swapUID === swapUID ? { ...s, status: "cancelled" } : s
          )
        );

        toast.current?.show({
          severity: "success",
          summary: "Swap cancelled",
          detail: "The swap was cancelled successfully.",
          life: 3000,
        });

        await new Promise((resolve) =>
          setTimeout(resolve, SWAP_ROW_FADE_OUT_MS)
        );

        setFilteredRows((prev) => prev.filter((s) => s.swapUID !== swapUID));
        setRowPhases((prev) => {
          const next = { ...prev };
          delete next[swapUID];
          return next;
        });
      } catch (err) {
        setRowPhases((prev) => {
          const next = { ...prev };
          delete next[swapUID];
          return next;
        });

        const detail =
          err instanceof Error ? err.message : "Could not cancel this swap.";
        toast.current?.show({
          severity: "error",
          summary: "Cancel failed",
          detail,
          life: 4000,
        });
        throw err;
      }
    },
    [toast, setBannerSwapQueue, setFilteredRows]
  );

  const getRowClassName = useCallback(
    (rowData: BannerSwap) => {
      const phase = rowPhases[rowData.swapUID];
      if (phase === "cancelling") {
        return "banner-swap-row-cancelling";
      }
      if (phase === "removing") {
        return "banner-swap-row-removing";
      }
      return undefined;
    },
    [rowPhases]
  );

  const isSwapCancelling = useCallback(
    (swapUID: string) => Boolean(rowPhases[swapUID]),
    [rowPhases]
  );

  return { cancelSwap, getRowClassName, isSwapCancelling };
}
